import { useEffect, useState } from "react";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { Bse2Badge } from "@/components/Bse2Badge";
import { formatCurrency, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  sip: { field: "sip_reg_id", endpoint: "/admin/sips" },
  xsip: { field: "xsip_reg_id", endpoint: "/admin/xsips" },
  stp: { field: "stp_reg_no", endpoint: "/admin/stps" },
  swp: { field: "swp_reg_no", endpoint: "/admin/swps" },
};

function Row({ item, kind, onRefresh, refreshing }) {
  const regId = item[KIND_META[kind].field];
  const label = item.scheme_name || `${item.from_scheme_name} \u2192 ${item.to_scheme_name}`;
  return (
    <div className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-${kind}-row-${regId}`}>
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-xs text-[var(--ink-muted)]">Client {item.client_code} &middot; {item.frequency_type} &middot; {formatCurrency(item.installment_amount)} &middot; Reg# {regId}</div>
      </div>
      <div className="flex flex-wrap items-center gap-2"><StatusBadge status={item.status} /><Bse2Badge bse2={item.bse2} />{item.bse2?.auth_link && <Button asChild size="sm" className="rounded-full bg-[var(--cta)]"><a href={item.bse2.auth_link} target="_blank" rel="noreferrer"><ExternalLink className="mr-1 h-3.5 w-3.5" /> Investor auth</a></Button>}{item.bse2?.reg_no && <Button size="sm" variant="outline" className="rounded-full" disabled={!!refreshing} onClick={() => onRefresh(kind, regId)}><RefreshCw className={`mr-1 h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> BSE status</Button>}</div>
    </div>
  );
}

export default function SipsMonitor() {
  const [data, setData] = useState({ sip: [], xsip: [], stp: [], swp: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState("");

  const load = () => { setLoading(true); return Promise.all(Object.entries(KIND_META).map(async ([kind, meta]) => {
    const response = await adminApi.get(meta.endpoint); return [kind, response.data];
  })).then((entries) => setData(Object.fromEntries(entries))).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const refreshBse = async (kind, regId) => {
    const key = `${kind}-${regId}`;
    setRefreshing(key);
    try {
      const { data } = await adminApi.post(`/admin/systematic/${kind}/${regId}/bse-refresh`);
      toast.success(`BSE ${kind.toUpperCase()} status: ${data.live_status || "refreshed"}`);
      await load();
    } catch (error) { toast.error(getApiErrorMessage(error)); }
    finally { setRefreshing(""); }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between"><div><h1 className="font-heading text-3xl text-[var(--brand)]">Systematic Plans Monitoring</h1><p className="mt-1 text-sm text-[var(--ink-muted)]">SIP, XSIP, STP and SWP registrations returned by the real transaction service.</p></div><Button variant="outline" size="sm" className="rounded-full" disabled={loading} onClick={load}><RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh</Button></div>
      <Tabs defaultValue="sip">
        <TabsList>
          <TabsTrigger value="sip" data-testid="admin-tab-sip">SIPs ({data.sip.length})</TabsTrigger>
          <TabsTrigger value="xsip" data-testid="admin-tab-xsip">XSIPs ({data.xsip.length})</TabsTrigger>
          <TabsTrigger value="stp" data-testid="admin-tab-stp">STPs ({data.stp.length})</TabsTrigger>
          <TabsTrigger value="swp" data-testid="admin-tab-swp">SWPs ({data.swp.length})</TabsTrigger>
        </TabsList>
        {Object.keys(KIND_META).map((kind) => (
          <TabsContent key={kind} value={kind}>
            <Card className="border-[var(--glass-border)]"><CardContent className="p-0">
              {loading ? <div className="p-6 text-sm text-[var(--ink-muted)]">Loading exchange plans…</div> : data[kind].length === 0 ? <div className="p-6 text-sm text-[var(--ink-muted)]" data-testid={`admin-no-${kind}s`}>No {kind.toUpperCase()}s registered.</div> :
                <div className="divide-y divide-[var(--glass-border)]">{data[kind].map((item) => { const id = item[KIND_META[kind].field]; return <Row key={id} item={item} kind={kind} onRefresh={refreshBse} refreshing={refreshing === `${kind}-${id}`} />; })}</div>}
            </CardContent></Card>
          </TabsContent>
        ))}
      </Tabs>
    </AdminLayout>
  );
}
